import { TENANT } from "./tenant";
import { createSupportFetch, type ReasonPrompt } from "./support-view";

// All API calls go through the Vite dev-proxy (`/api` → http://localhost:3000)
// so the browser stays same-origin in dev. In prod the same path is rewritten
// by the reverse proxy that fronts both the static build and the API.
export const API_BASE = "/api";

export function tenantHeader(): Record<string, string> {
  return { "X-Tenant-Id": TENANT.id };
}

// CAR-36: the clinician JWT now rides as an httpOnly session cookie set by the
// API — never a bearer header, never readable from JS. Every authed fetch must
// send `credentials: 'include'` so the browser attaches the cookie. Spread
// this into the fetch options of any clinician request.
//
// CAR-95: also `cache: 'no-store'`. Before the edge gained the /api → API
// proxy, admin GETs (/me, /health, /inbox, …) fell through to the SPA and
// returned a cacheable `200 text/html` (index fallback, ETag, no Cache-Control).
// The browser cached those and kept serving them from cache WITHOUT
// re-requesting — so even after the proxy landed, authed GETs got stale HTML,
// `res.json()` threw, and login/session/inbox silently broke. Bypassing the
// HTTP cache on every API call forces a live request. (Harmless on POSTs.)
export const withCredentials: Pick<RequestInit, "credentials" | "cache"> = {
  credentials: "include",
  cache: "no-store",
};

// TES-1040 — the reason dialog of the admin's support views, registered by
// SupportViewLayout while one of those sections is mounted.
let supportReasonPrompt: ReasonPrompt | null = null;
export function setSupportReasonPrompt(prompt: ReasonPrompt | null): void {
  supportReasonPrompt = prompt;
}

// TES-1040 — every API client fetches through this. Outside the admin's
// support views it is plain fetch; inside them it sends X-Support-View-As and
// turns a mid-session 403 `support_reason_required` into the reason dialog
// and one retry (see createSupportFetch).
export const apiFetch: typeof fetch = createSupportFetch({
  fetch: (input, init) => fetch(input, init),
  currentPath: () => window.location.pathname,
  prompt: () => supportReasonPrompt,
});
